import { prepareOrderMeasurement } from "@/lib/billing/checkout-routing";
import { guardMetaNavigation } from "@/lib/meta-pixel";
import { recoverFromChunkError } from "@/lib/chunk-reload";
import { matchComponentPagePath } from "@/lib/component-page-route";
import { matchResourceRoute } from "@/lib/resource-page-route";
import { matchPaidLandingRoute } from "@/lib/paid-landing-route";
import { captureAgentDraftArrival } from "@/lib/agent-tools/arrival";
import { isPricingPath } from "@/lib/pricing";
import { prefetchBillingConfig } from "@/lib/billing/config-request";
import { prefetchOfferRegion } from "@/lib/offer-receipt";
import { failStartupRecovery, installStartupRecovery } from "./startup-recovery";
import { captureEmailAttribution } from "./email-attribution";
import "./index.css";

installStartupRecovery();

// A deploy replaces every hashed chunk, so a tab left open across one asks for
// files that are gone. Nothing has mounted yet at this point, so there is no
// work to lose and a reload is the whole fix; `chunk-reload` caps it at one.
// Show recovery first, including when storage blocks the automatic retry.
const bootFailed = (error: unknown) => {
  failStartupRecovery();
  if (!recoverFromChunkError(error)) throw error;
};

function boot(): void {
  if (/^\/castor(?:\/index\.html)?\/?$/.test(window.location.pathname)) {
    void import("./castor-entry").then(({ mountCastor }) => mountCastor()).catch(bootFailed);
    return;
  }
  captureEmailAttribution();
  // The private studio never enters marketing analytics or canvas startup.
  if (/^\/email-studio\/?$/.test(window.location.pathname)) {
    void import("./email-studio-entry").then(({ mountEmailStudio }) => mountEmailStudio()).catch(bootFailed);
    return;
  }
  // Private document links never enter marketing analytics or project autosave.
  if (/^\/handover\/?$/.test(window.location.pathname)) {
    const token = window.location.hash.slice(1);
    void import("./handover-entry").then(({ mountHandover }) => mountHandover(token)).catch(bootFailed);
    return;
  }
  prepareOrderMeasurement();
  guardMetaNavigation();
  captureAgentDraftArrival();
  window.addEventListener("hashchange", captureAgentDraftArrival);

  // These read-only requests do not need React, a session or an assignment.
  // Start them together before downloading the pricing application.
  if (isPricingPath(window.location.pathname)) {
    prefetchBillingConfig();
    prefetchOfferRegion();
  }

  const componentPageRoute = matchComponentPagePath(window.location.pathname);
  const resourcePageRoute = matchResourceRoute(window.location.pathname);
  const paidLandingRoute = matchPaidLandingRoute(window.location.pathname);

  if (paidLandingRoute) {
    void import("@/paid-landing-entry")
      .then(({ mountPaidLandingPage }) => mountPaidLandingPage(paidLandingRoute))
      .catch(bootFailed);
  } else if (resourcePageRoute) {
    // The useful page body is already in the document; load its interactive
    // controls without routing through the canvas application.
    void import("@/resource-page-entry")
      .then(({ mountResourcePage }) => {
        mountResourcePage(resourcePageRoute);
      })
      .catch(bootFailed);
  } else if (componentPageRoute) {
    // The page is already server-rendered; let it paint before React loads over it.
    const enhanceComponentPage = () => {
      void import("@/component-page-entry")
        .then(({ mountComponentPage }) => {
          mountComponentPage(componentPageRoute);
        })
        .catch(bootFailed);
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(enhanceComponentPage, { timeout: 1_500 });
    } else {
      globalThis.addEventListener("load", enhanceComponentPage, { once: true });
    }
  } else {
    // Returned, not voided: `mountApp` awaits the route chunk, and that import is
    // the one that blanked `/terms` (12V-7Z).
    void import("@/app-entry")
      .then(({ mountApp }) => mountApp())
      .catch(bootFailed);
  }
}

try {
  boot();
} catch (error) {
  // Chunk retry recognizes generic Safari fetch wording, so reserve it for
  // import rejections. A synchronous startup error still gets visible recovery.
  failStartupRecovery();
  throw error;
}
